import { useMemo } from 'react';
import { usePledgePreferencesQuery } from '@/hooks/queries/usePledgePreferencesQuery';
import { usePledgeSheetsQuery, PledgeSheet } from '@/hooks/queries/usePledgeSheetsQuery';
import { calculateWeeklyPointsFromPledgeSheet, PledgeSheetData } from '@/lib/pledgeBasedPlan';
import { useApp } from '@/context/AppContext';

/**
 * Converts a PledgeSheet (from DB) to PledgeSheetData format (for calculation)
 */
function toPledgeSheetData(sheet: PledgeSheet): PledgeSheetData {
  return {
    minimumFyc: sheet.minimum_fyc,
    stretchedFyc: sheet.stretched_fyc,
    averageCaseSize: sheet.average_case_size,
    closingRate: sheet.closing_rate,
    openingToClosingRate: sheet.opening_to_closing_rate,
    callToOpeningRate: sheet.call_to_opening_rate,
    monthlyTargets: sheet.monthly_targets,
    annualTargets: sheet.annual_appointment_sets != null ? {
      appointment_sets: sheet.annual_appointment_sets ?? 0,
      opening_interviews: sheet.annual_opening_interviews ?? 0,
      closing_interviews: sheet.annual_closing_interviews ?? 0,
      life_cases_needed: sheet.annual_life_cases_needed ?? 0,
    } : undefined,
    selectedMonths: sheet.selected_months ?? undefined,
  };
}

export interface PledgeBasedGoalResult {
  weeklyGoal: number;
  dailyGoal: number;
  isFromPledge: boolean;
  loading: boolean;
  fycTarget: number | null;
  averageCaseSize: number | null;
}

/**
 * Hook that calculates the daily goal from a linked pledge sheet, 
 * or falls back to the profile's weekly_goal setting.
 */
export function usePledgeBasedDailyGoal(): PledgeBasedGoalResult {
  const { state } = useApp();
  const { selectedPledgeSheetId, useStretchedGoals, loading: prefsLoading } = usePledgePreferencesQuery();
  const { pledgeSheets, loading: sheetsLoading } = usePledgeSheetsQuery();

  const result = useMemo(() => {
    // Resolve the effective sheet: prefer selected, fallback to most recent
    const effectiveSheetId = selectedPledgeSheetId || (pledgeSheets.length > 0 ? pledgeSheets[0].id : null);
    
    if (effectiveSheetId) {
      const sheet = pledgeSheets.find((s) => s.id === effectiveSheetId);
      if (sheet) {
        const pledgeData = toPledgeSheetData(sheet);
        const calculated = calculateWeeklyPointsFromPledgeSheet(pledgeData, useStretchedGoals);
        const weeklyGoal = calculated.totalPoints;
        const dailyGoal = Math.max(1, Math.ceil(weeklyGoal / 7));
        const fycTarget = useStretchedGoals ? sheet.stretched_fyc : sheet.minimum_fyc;
        const averageCaseSize = sheet.average_case_size ?? null;
        return { weeklyGoal, dailyGoal, isFromPledge: true, fycTarget, averageCaseSize };
      }
    }

    // Fallback to profile value or default
    const weeklyGoal = state.user?.weeklyGoal || state.weeklyGoal || 300;
    const dailyGoal = Math.max(1, Math.ceil(weeklyGoal / 7));
    return { weeklyGoal, dailyGoal, isFromPledge: false, fycTarget: null, averageCaseSize: null };
  }, [selectedPledgeSheetId, pledgeSheets, useStretchedGoals, state.user?.weeklyGoal, state.weeklyGoal]);

  return {
    ...result,
    loading: prefsLoading || sheetsLoading,
  };
}
